// Live microphone → Lumina Voice real-time transcription.
// Captures mic audio, downsamples to 16kHz linear16 PCM, and streams it over a
// WebSocket to our FastAPI bridge (/api/lumina-voice/stream), which relays to
// the upstream Deepgram-backed engine. Transcript events stream back as JSON.
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export function micSupported() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && (window.AudioContext || window.webkitAudioContext));
}

export async function startLuminaVoice({ token, language = "en", diarize = false, onFinal, onInterim, onError, onClose }) {
  if (!micSupported()) throw new Error("Live microphone is not supported in this browser — type the encounter instead.");

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AudioCtx();
  const source = audioCtx.createMediaStreamSource(stream);
  const processor = audioCtx.createScriptProcessor(4096, 1, 1);
  const silent = audioCtx.createGain();
  silent.gain.value = 0; // route through a muted node so nothing plays back
  const inRate = audioCtx.sampleRate;
  const outRate = 16000;

  // DA-74b — nova-3-medical is English-only; use general nova-3 for other languages and for multilingual auto-detect.
  const model = (language === "multi" || !/^en/i.test(language)) ? "nova-3" : "nova-3-medical";
  const qs = new URLSearchParams({
    token: token || "",
    encoding: "linear16",
    sample_rate: String(outRate),
    language,
    model,
    punctuate: "true",
    interim_results: "true",
    smart_format: "true",
  });
  // A.3 — Multi-Speaker: only requested for Ambient; the relay forwards it as-is.
  if (diarize) qs.set("diarize", "true");
  const ws = new WebSocket(`${WS_BASE}/api/lumina-voice/stream?${qs.toString()}`);
  ws.binaryType = "arraybuffer";

  let paused = false, closed = false, keepAlive = null;

  ws.onopen = () => {
    keepAlive = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "KeepAlive" })); }, 8000);
  };
  ws.onmessage = (ev) => {
    let d; try { d = JSON.parse(ev.data); } catch { return; }
    if (d.type === "Results") {
      const alt = d.channel?.alternatives?.[0] || {};
      const t = (alt.transcript || "").trim();
      if (!t) return;
      if (d.is_final) {
        // A.4 / DA-74 — the segment's speaker is the FIRST word's speaker (portal parity),
        // not the most common; timings come from the word span.
        let meta = null;
        const words = alt.words || [];
        if (words.length && words[0].speaker != null) {
          meta = { speaker: +words[0].speaker, start: words[0].start, end: words[words.length - 1].end };
        }
        onFinal?.(t, meta);
      } else onInterim?.(t);
    } else if (d.type === "Error" || d.type === "error") {
      onError?.(d.description || d.message || "transcription stream error");
    }
  };
  ws.onerror = () => onError?.("Could not reach the transcription service.");
  ws.onclose = () => { clearInterval(keepAlive); onClose?.(); };

  const downsampleToPCM16 = (input) => {
    const ratio = inRate / outRate;
    const outLen = Math.floor(input.length / ratio);
    const out = new Int16Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)] || 0));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  };

  processor.onaudioprocess = (e) => {
    if (paused || closed || ws.readyState !== 1) return;
    ws.send(downsampleToPCM16(e.inputBuffer.getChannelData(0)).buffer);
  };

  source.connect(processor);
  processor.connect(silent);
  silent.connect(audioCtx.destination);

  const teardown = () => {
    closed = true;
    try { processor.disconnect(); } catch {}
    try { source.disconnect(); } catch {}
    try { silent.disconnect(); } catch {}
    try { stream.getTracks().forEach((t) => t.stop()); } catch {}
    try { audioCtx.close(); } catch {}
  };

  return {
    pause() { paused = true; try { audioCtx.suspend(); } catch {} },
    resume() { paused = false; try { audioCtx.resume(); } catch {} },
    async stop() {
      teardown();
      try { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "CloseStream" })); } catch {}
      await new Promise((r) => setTimeout(r, 700)); // let the final Results land
      try { ws.close(); } catch {}
    },
  };
}
